"use client";

/**
 * useAuthConnectState — the connect-button state machine shared by the two
 * in-chat auth surfaces (AuthCard in ToolUseBlock, ComposioAuthInput).
 *
 * The OAuth dance happens in another tab and only ever signals us on
 * SUCCESS (callback page → /verify → substrate resolution). Cancelling on
 * the provider's consent screen produces no callback at all, so 'connecting'
 * can never be treated as a durable fact — it is a claim we re-verify every
 * time the user comes back to this tab.
 *
 * Signals:
 *  - markClicked()      — user clicked Connect; OAuth tab is opening.
 *  - resetToIdle()      — caller-side abort (popup blocked, initiate threw).
 *  - probe on mount     — revisit case: toolkit already connected → 'connected'.
 *  - probe on tab return (visibilitychange→visible / window focus):
 *      · connected → 'connected' (the pre-verify flash before the substrate
 *        resolves and unmounts the caller).
 *      · NOT connected while 'connecting' → the user may have abandoned the
 *        flow — OR their /verify just hasn't landed yet (the callback tab
 *        awaits /verify before auto-closing, so a fast tab-switch races it).
 *        We re-probe on a short grace schedule; only if every recheck stays
 *        negative do we conclude "abandoned" and reset to 'idle' so the
 *        Connect button comes back.
 *
 * The probe itself is the deduped single-flight helper in
 * lib/connections-probe (2s freshness window) — N mounted cards on one
 * toolkit still cost one request per burst.
 *
 * The probe is parameterized (default: probeIsToolkitConnected) so non-
 * Composio callers — e.g. OAuthConnectInput's MCP `/api/mcps/oauth/status`
 * probe — can plug in their own "is this key connected?" check while
 * reusing the same settle/grace-recheck state machine. Composio/AuthCard
 * callers pass a single `key` arg and get the default, so their behavior is
 * unchanged.
 *
 * `pollWhileConnectingMs` (opt-in; the Connect GitHub card): also probe on that
 * cadence while the person is connecting and this tab is visible. GitHub's
 * install finishes in another tab or window, and a window side by side with
 * this one sends neither a focus nor a visibility event until it is clicked.
 * A poll only ever flips to 'connected': a negative tick is not a tab return,
 * so it never starts the abandon reset.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { probeIsToolkitConnected } from "@/lib/connections-probe";

/** Grace rechecks after a negative tab-return probe, before declaring the
 *  flow abandoned. Two beats of 2.5s ≈ 5s total — enough for the callback
 *  tab's /verify round-trip, short enough that a genuine cancel doesn't
 *  leave the user staring at a spinner. Each delay exceeds the probe's 2s
 *  freshness window so every recheck hits the network. */
const ABANDON_RECHECK_DELAYS_MS = [2500, 2500];

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export type AuthConnectStatus = "idle" | "connecting" | "connected";

export function useAuthConnectState(
  key: string,
  probe: (
    token: string,
    key: string
  ) => Promise<boolean> = probeIsToolkitConnected,
  { pollWhileConnectingMs }: { pollWhileConnectingMs?: number } = {}
): {
  status: AuthConnectStatus;
  markClicked: () => void;
  resetToIdle: () => void;
} {
  const { session } = useAuth();
  const [userClicked, setUserClicked] = useState(false);
  const [connectedFromQuery, setConnectedFromQuery] = useState(false);

  // Event listeners and the grace loop need the CURRENT click state, not the
  // value captured when the effect mounted.
  const userClickedRef = useRef(false);
  // Monotonic token: bumping it invalidates any in-flight grace loop (a
  // newer tab-return supersedes an older one; a manual reset cancels all).
  const graceToken = useRef(0);

  const markClicked = useCallback(() => {
    userClickedRef.current = true;
    setUserClicked(true);
  }, []);

  const resetToIdle = useCallback(() => {
    graceToken.current++;
    userClickedRef.current = false;
    setUserClicked(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const token = session?.access_token;
    if (!token) return;

    // One settle pass: probe now; on a negative result mid-flow, walk the
    // grace schedule before concluding the user abandoned the OAuth tab.
    const settle = async () => {
      const connected = await probe(token, key);
      if (cancelled) return;
      if (connected) {
        setConnectedFromQuery(true);
        return;
      }
      if (!userClickedRef.current) return;

      const myToken = ++graceToken.current;
      for (const delay of ABANDON_RECHECK_DELAYS_MS) {
        await sleep(delay);
        if (
          cancelled ||
          graceToken.current !== myToken ||
          !userClickedRef.current
        )
          return;
        const nowConnected = await probe(token, key);
        if (cancelled || graceToken.current !== myToken) return;
        if (nowConnected) {
          setConnectedFromQuery(true);
          return;
        }
      }
      // Every recheck stayed negative — the flow was abandoned. Offer
      // Connect again.
      userClickedRef.current = false;
      setUserClicked(false);
    };

    void settle();
    const onVisible = () => {
      if (document.visibilityState === "visible") void settle();
    };
    // `focus` covers OAuth opened in a separate WINDOW: side-by-side windows
    // never fire visibilitychange, but refocusing ours fires this.
    const onFocus = () => {
      void settle();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, [key, probe, session?.access_token]);

  useEffect(() => {
    const token = session?.access_token;
    if (!pollWhileConnectingMs || !token || !userClicked || connectedFromQuery)
      return;
    let cancelled = false;
    let inFlight = false;
    const tick = async () => {
      if (inFlight || document.visibilityState !== "visible") return;
      inFlight = true;
      try {
        if ((await probe(token, key)) && !cancelled)
          setConnectedFromQuery(true);
      } finally {
        inFlight = false;
      }
    };
    const timer = setInterval(() => void tick(), pollWhileConnectingMs);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [
    pollWhileConnectingMs,
    userClicked,
    connectedFromQuery,
    key,
    probe,
    session?.access_token,
  ]);

  const status: AuthConnectStatus = connectedFromQuery
    ? "connected"
    : userClicked
      ? "connecting"
      : "idle";

  return { status, markClicked, resetToIdle };
}
